// ImageWorkspacePage — the dedicated /images conversational image
// workspace (Owner 19-10, MR C). NOT a modal: a first-class page inside
// the normal RealityAI shell (sidebar + main area).
//
// Interaction model (GPT-like, RealityAI identity):
//   * empty state: minimal header + quota + six semantic shortcut cards
//     (3x2) + central composer;
//   * after the first request: cards collapse, the conversation timeline
//     is the primary content, the composer is sticky at the bottom;
//   * the user can always just type a prompt — cards are shortcuts, not
//     mandatory tabs;
//   * capability truth comes from the server registry: only AVAILABLE
//     operations are interactive; the rest show «Скоро» (never a
//     clickable promise the production provider cannot keep).
//
// Never rendered: worker/queue/ComfyUI/FLUX/provider internals, preset
// identifiers, filesystem paths. User copy only («Создаю изображение…»).
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { X, Download, RotateCw, Share2, Maximize2, Copy, AlertCircle, Pencil, Images } from "lucide-react";
import { cn } from "@/lib/utils";
import { Tip } from "@/components/Tip";
import { ComposerCore, type PendingAttachment } from "@/components/composer/ComposerCore";
import {
  IMAGE_MODE_CARDS,
  cardState,
  cardOperation,
  type ImageModeCard,
} from "@/lib/images/modes";
import {
  cancelGeneration,
  createGeneration,
  downloadGenerationImage,
  fetchGeneration,
  fetchGenerationImageBlob,
  fetchGenerationImageBlobUrl,
  fetchImageGenCapability,
  listGenerations,
  type ImageGenCapability,
  type ImageGenJob,
} from "@/lib/imagegen/api";
import { ApiError, type AttachmentPayload } from "@/lib/api";

// crypto.randomUUID exists only in secure contexts (https/localhost);
// DEV over plain http needs a fallback (same shape, client-side
// idempotency nonce only — the server treats it as an opaque key).
function uuid(): string {
  if (typeof crypto !== "undefined" && typeof crypto.randomUUID === "function") {
    return crypto.randomUUID();
  }
  return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
  });
}

// Composer thumbnail for a seeded result attachment (cosmetic only).
function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(blob);
  });
}

const IN_FLIGHT = new Set(["queued", "running"]);

// User-facing operation labels (semantic only — never provider terms).
const OPERATION_LABELS: Record<string, string> = {
  text_to_image: "Генерация",
  image_edit: "Редактирование",
  scene_change: "Изменение фона",
  object_replacement: "Замена объекта",
  single_reference: "По образцу",
  multi_reference: "По образцу",
  layout_bbox: "Композиция",
};

function defaultPreset(capability: ImageGenCapability | null): string {
  // The backend preset allowlist is the authority; prefer square_768
  // (the accepted production default), else the first approved preset.
  const codes = capability?.presets ?? [];
  return codes.find((p) => p.code === "square_768")?.code ?? codes[0]?.code ?? "square_768";
}

export function ImageWorkspacePage() {
  const [capability, setCapability] = useState<ImageGenCapability | null>(null);
  const [capabilityLoaded, setCapabilityLoaded] = useState(false);
  const [jobs, setJobs] = useState<ImageGenJob[]>([]);
  const [jobsLoaded, setJobsLoaded] = useState(false);
  const [selectedCard, setSelectedCard] = useState<ImageModeCard | null>(null);
  const [errorLine, setErrorLine] = useState<string | null>(null);
  const pollRef = useRef<number | null>(null);
  const scrollRef = useRef<HTMLDivElement | null>(null);

  // Page title (browser tab): «Изображения — RealityAI».
  useEffect(() => {
    const prev = document.title;
    document.title = "Изображения — RealityAI";
    return () => {
      document.title = prev;
    };
  }, []);

  useEffect(() => {
    fetchImageGenCapability()
      .then(setCapability)
      .catch(() => setCapability(null))
      .finally(() => setCapabilityLoaded(true));
    listGenerations(50)
      .then(setJobs)
      .catch(() => setJobs([]))
      .finally(() => setJobsLoaded(true));
  }, []);

  // Poll in-flight jobs (the backend is the only lifecycle authority).
  useEffect(() => {
    const inFlight = jobs.filter((j) => IN_FLIGHT.has(j.status));
    if (inFlight.length === 0) return;
    pollRef.current = window.setInterval(async () => {
      const updates = await Promise.all(
        inFlight.map((j) => fetchGeneration(j.id).catch(() => null))
      );
      setJobs((prev) =>
        prev.map((j) => updates.find((u) => u && u.id === j.id) ?? j)
      );
    }, 3000);
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, [jobs]);

  // Keep the conversation pinned to the newest turn.
  const timelineEndRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    // jsdom (tests) has no scrollIntoView — guard, never crash.
    timelineEndRef.current?.scrollIntoView?.({ block: "end" });
  }, [jobs.length]);

  const hasConversation = jobs.length > 0;

  const handleSend = useCallback(
    async (query: string, attachments?: AttachmentPayload[]) => {
      setErrorLine(null);
      const operation = selectedCard ? cardOperation(selectedCard) : undefined;
      try {
        const job = await createGeneration({
          prompt: query,
          preset: defaultPreset(capability),
          idempotencyKey: uuid(),
          operation,
          attachments: (attachments ?? [])
            .filter((a): a is AttachmentPayload & { file_id: string } => !!a.file_id)
            .map((a) => ({
              file_id: a.file_id,
              role: selectedCard && ["image_edit", "scene_change", "object_replacement"].includes(selectedCard.operation) ? "input" : "reference",
            })),
        });
        setJobs((prev) => [...prev, job]);
      } catch (e) {
        setErrorLine(
          e instanceof ApiError
            ? e.message || "Не удалось создать задание."
            : "Не удалось создать задание."
        );
      }
    },
    [capability, selectedCard]
  );

  const handleCancel = useCallback(async (jobId: string) => {
    try {
      const j = await cancelGeneration(jobId);
      setJobs((prev) => prev.map((x) => (x.id === jobId ? j : x)));
    } catch {
      /* terminal-state conflict: the next poll converges the card */
    }
  }, []);

  const handleRegenerate = useCallback(
    async (job: ImageGenJob) => {
      setErrorLine(null);
      try {
        const fresh = await createGeneration({
          prompt: job.prompt,
          preset: job.preset,
          idempotencyKey: uuid(),
          operation: job.operation ?? undefined,
          attachments: (job.attachments ?? []).map((a) => ({ file_id: a.file_id, role: a.role })),
        });
        setJobs((prev) => [...prev, fresh]);
      } catch (e) {
        setErrorLine(e instanceof ApiError ? e.message || "Не удалось повторить." : "Не удалось повторить.");
      }
    },
    []
  );

  const maxAttachments = capability?.max_reference_images ?? 4;

  // Continue-editing flows (MR D): a previous result is reused via its
  // result_file_id — the file is already RealityAI-owned, so it is
  // seeded straight into the composer context WITHOUT any re-upload.
  // The buttons only appear when the server registry marks the target
  // operation AVAILABLE (never a clickable promise the provider can't keep).
  const [seeded, setSeeded] = useState<PendingAttachment[] | null>(null);
  const availableOps = useMemo(
    () => new Set((capability?.operations ?? [{ code: "text_to_image" }]).map((o) => o.code)),
    [capability]
  );

  const seedFromResult = useCallback(
    async (job: ImageGenJob, mode: "edit" | "reference") => {
      if (!job.result_file_id) return;
      setErrorLine(null);
      // Thumbnail only — bytes never round-trip through an upload.
      let data_url: string | undefined;
      try {
        const blob = await fetchGenerationImageBlob(job.id);
        data_url = await blobToDataUrl(blob);
      } catch {
        /* thumbnail is cosmetic; the file_id is what the next request uses */
      }
      const att: PendingAttachment = {
        name: `realityai-${job.id.slice(0, 8)}.png`,
        size: 0,
        mime: "image/png",
        kind: "image",
        file_id: job.result_file_id,
        ...(data_url ? { data_url } : {}),
      };
      setSeeded((prev) => [...(prev ?? []), att]);
      // Pre-select the semantic card so the role mapping is correct.
      setSelectedCard(
        mode === "edit"
          ? IMAGE_MODE_CARDS.find((c) => c.operation === "image_edit") ?? null
          : IMAGE_MODE_CARDS.find((c) => c.id === "reference") ?? null
      );
    },
    []
  );

  const quotaText = useMemo(() => {
    if (!capability || !capability.entitled) return null;
    if (capability.unlimited) return "Лимит: без ограничений";
    if (capability.remaining_today != null)
      return `Сегодня: ${capability.remaining_today} из ${capability.daily_limit} осталось`;
    return null;
  }, [capability]);

  const featureUnavailable = capabilityLoaded && capability === null;
  const notEntitled = capabilityLoaded && capability !== null && !capability.entitled;

  return (
    <div className="flex flex-col h-full relative" data-testid="images-page">
      {/* Header */}
      <header className="shrink-0 px-6 pt-5 pb-2 flex items-center justify-between">
        <h1 className="text-lg font-semibold text-primary">Изображения</h1>
        {quotaText && (
          <span className="text-xs text-tertiary" data-testid="images-quota">
            {quotaText}
          </span>
        )}
      </header>

      {/* Main scroll area */}
      <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 md:px-6">
        <div className="max-w-3xl mx-auto w-full">
          {featureUnavailable ? (
            <div className="mt-24 text-center text-secondary text-sm">
              Изображения временно недоступны на вашем тарифе.
            </div>
          ) : (
            <>
              {/* Empty state: six shortcut cards (3x2 desktop) */}
              {!hasConversation && (
                <div className="mt-10 mb-8" data-testid="images-cards">
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                    {IMAGE_MODE_CARDS.map((card) => {
                      const state = cardState(card, capability);
                      const Icon = card.icon;
                      const interactive = state === "available";
                      return (
                        <button
                          key={card.id}
                          data-testid={`mode-card-${card.id}`}
                          disabled={!interactive}
                          onClick={() =>
                            setSelectedCard((cur) => (cur?.id === card.id ? null : card))
                          }
                          className={cn(
                            "text-left rounded-xl border p-4 transition-all",
                            interactive
                              ? "border-strongline bg-surface hover:border-accent/60 hover:shadow-[0_0_20px_rgba(34,211,238,0.10)]"
                              : "border-hairline bg-surface/50 opacity-60 cursor-default",
                            selectedCard?.id === card.id &&
                              "border-accent ring-1 ring-accent/50"
                          )}
                        >
                          <div className="flex items-center gap-2">
                            <Icon size={18} strokeWidth={1.8} className="text-accent" />
                            <span className="text-sm font-medium text-primary">
                              {card.title}
                            </span>
                            {!interactive && (
                              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded bg-hoverbg text-tertiary">
                                Скоро
                              </span>
                            )}
                          </div>
                          <p className="mt-1.5 text-xs text-tertiary leading-relaxed">
                            {card.description}
                          </p>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Conversation timeline (chronological) */}
              <div className="flex flex-col gap-4 pb-4" data-testid="images-timeline">
                {jobs.map((job) => (
                  <TimelineTurn
                    key={job.id}
                    job={job}
                    canEdit={availableOps.has("image_edit")}
                    canReference={availableOps.has("single_reference") || availableOps.has("multi_reference")}
                    onCancel={() => void handleCancel(job.id)}
                    onRegenerate={() => void handleRegenerate(job)}
                    onEdit={() => void seedFromResult(job, "edit")}
                    onReference={() => void seedFromResult(job, "reference")}
                  />
                ))}
                <div ref={timelineEndRef} />
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky composer */}
      <div className="shrink-0 px-4 md:px-6 pt-2 pb-4 bg-gradient-to-t from-app via-app to-transparent">
        {errorLine && (
          <div className="max-w-3xl mx-auto mb-2 text-xs text-red-400 flex items-center gap-1.5" data-testid="images-error">
            <AlertCircle size={13} /> {errorLine}
          </div>
        )}
        {notEntitled ? (
          <div className="max-w-3xl mx-auto text-center text-sm text-secondary bg-surface border border-strongline rounded-xl py-3">
            Изображения недоступны на вашем тарифе.
          </div>
        ) : (
          <ComposerCore
            onSend={(q, atts) => {
              setSeeded(null);
              void handleSend(q, atts);
            }}
            initialAttachments={seeded ?? undefined}
            placeholder="Опишите изображение или продолжите диалог…"
            accept="image/png,image/jpeg,image/webp"
            enableDrop
            maxAttachments={maxAttachments}
            topSlot={
              selectedCard ? (
                <div className="mb-2 px-1" data-testid="mode-chip">
                  <span className="inline-flex items-center gap-1.5 pl-2.5 pr-1 h-7 rounded-full bg-accent/10 text-accent text-xs font-medium border border-accent/30">
                    {selectedCard.title}
                    <button
                      onClick={() => setSelectedCard(null)}
                      className="p-0.5 rounded-full hover:bg-accent/20"
                      aria-label="Сбросить режим"
                    >
                      <X size={12} />
                    </button>
                  </span>
                </div>
              ) : hasConversation ? (
                // Cards collapsed: compact mode chooser near the composer.
                <div className="mb-2 px-1 flex flex-wrap gap-1.5" data-testid="mode-compact">
                  {IMAGE_MODE_CARDS.filter((c) => cardState(c, capability) === "available").map((c) => (
                    <button
                      key={c.id}
                      onClick={() => setSelectedCard((cur) => (cur?.id === c.id ? null : c))}
                      className={cn(
                        "px-2.5 h-7 rounded-full text-xs border transition-colors",
                        "border-strongline text-secondary hover:text-primary hover:border-accent/50"
                      )}
                    >
                      {c.title}
                    </button>
                  ))}
                </div>
              ) : undefined
            }
          />
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// One conversation turn: user bubble (right) + status/result card (left)
// ---------------------------------------------------------------------------

function TimelineTurn({
  job,
  canEdit,
  canReference,
  onCancel,
  onRegenerate,
  onEdit,
  onReference,
}: {
  job: ImageGenJob;
  canEdit: boolean;
  canReference: boolean;
  onCancel: () => void;
  onRegenerate: () => void;
  onEdit: () => void;
  onReference: () => void;
}) {
  return (
    <div className="flex flex-col gap-2" data-testid={`turn-${job.id}`}>
      {/* User request */}
      <div className="self-end max-w-[85%] bg-accent/10 border border-accent/20 rounded-2xl rounded-br-md px-4 py-2.5">
        {job.attachments && job.attachments.length > 0 && (
          <div className="text-[10px] text-tertiary mb-1">
            {job.attachments.length} изобр.
            {job.operation && OPERATION_LABELS[job.operation]
              ? ` · ${OPERATION_LABELS[job.operation]}`
              : ""}
          </div>
        )}
        <p className="text-sm text-primary whitespace-pre-wrap break-words">{job.prompt}</p>
      </div>

      {/* RealityAI side */}
      {IN_FLIGHT.has(job.status) ? (
        <div className="self-start max-w-[85%] bg-surface border border-strongline rounded-2xl rounded-bl-md px-4 py-3 flex items-center gap-3">
          <span className="w-4 h-4 rounded-full border-2 border-accent border-t-transparent animate-spin shrink-0" />
          <span className="text-sm text-secondary">Создаю изображение…</span>
          <button
            onClick={onCancel}
            className="ml-2 text-xs text-tertiary hover:text-primary underline-offset-2 hover:underline"
          >
            Отменить
          </button>
        </div>
      ) : job.status === "succeeded" ? (
        <ResultCard
          job={job}
          canEdit={canEdit}
          canReference={canReference}
          onRegenerate={onRegenerate}
          onEdit={onEdit}
          onReference={onReference}
        />
      ) : (
        <div className="self-start max-w-[85%] bg-surface border border-strongline rounded-2xl rounded-bl-md px-4 py-3 text-sm text-secondary">
          {job.status === "cancelled"
            ? "Генерация отменена."
            : job.error?.message || "Генерация не удалась. Попробуйте позже."}
        </div>
      )}
    </div>
  );
}

function ResultCard({
  job,
  canEdit,
  canReference,
  onRegenerate,
  onEdit,
  onReference,
}: {
  job: ImageGenJob;
  canEdit: boolean;
  canReference: boolean;
  onRegenerate: () => void;
  onEdit: () => void;
  onReference: () => void;
}) {
  const [url, setUrl] = useState<string | null>(null);
  const [fullscreen, setFullscreen] = useState(false);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let alive = true;
    let objUrl: string | null = null;
    fetchGenerationImageBlobUrl(job.id)
      .then((u) => {
        objUrl = u;
        if (alive) setUrl(u);
      })
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
      if (objUrl) URL.revokeObjectURL(objUrl);
    };
  }, [job.id]);

  const share = async () => {
    try {
      const blob = await fetchGenerationImageBlob(job.id);
      const file = new File([blob], `realityai-${job.id.slice(0, 8)}.png`, { type: blob.type || "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file] });
      } else if (url) {
        // No Web Share for files: copy the image to the clipboard where
        // supported, otherwise fall back to download.
        try {
          await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
        } catch {
          await downloadGenerationImage(job.id, `realityai-${job.id.slice(0, 8)}.png`);
        }
      }
    } catch {
      /* share canceled/unsupported */
    }
  };

  return (
    <div className="self-start max-w-[85%] bg-surface border border-strongline rounded-2xl rounded-bl-md overflow-hidden" data-testid={`result-${job.id}`}>
      {failed ? (
        <div className="p-4 text-sm text-secondary">Результат недоступен.</div>
      ) : (
        <button
          className="block w-full"
          onClick={() => setFullscreen(true)}
          aria-label="Открыть полноэкранный просмотр"
        >
          {url ? (
            <img src={url} alt="" className="max-h-[420px] w-auto object-contain bg-black/20" />
          ) : (
            <div className="w-[280px] h-[280px] bg-hoverbg animate-pulse" />
          )}
        </button>
      )}
      <div className="flex items-center gap-1 px-2 py-1.5 border-t border-hairline">
        <Tip label="Скачать" side="top">
          <button
            className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg"
            onClick={() => void downloadGenerationImage(job.id, `realityai-${job.id.slice(0, 8)}.png`)}
          >
            <Download size={15} />
          </button>
        </Tip>
        <Tip label="Повторить" side="top">
          <button className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg" onClick={onRegenerate}>
            <RotateCw size={15} />
          </button>
        </Tip>
        {/* Continue-editing (MR D): reuse result_file_id in the composer
            context — no download/re-upload round-trip. Rendered only
            when the server registry marks the operation available. */}
        {canEdit && job.result_file_id && (
          <Tip label="Редактировать" side="top">
            <button
              className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg"
              data-testid={`edit-${job.id}`}
              onClick={onEdit}
            >
              <Pencil size={15} />
            </button>
          </Tip>
        )}
        {canReference && job.result_file_id && (
          <Tip label="По образцу" side="top">
            <button
              className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg"
              data-testid={`reference-${job.id}`}
              onClick={onReference}
            >
              <Images size={15} />
            </button>
          </Tip>
        )}
        <Tip label="Поделиться" side="top">
          <button className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg" onClick={() => void share()}>
            <Share2 size={15} />
          </button>
        </Tip>
        <Tip label="Копировать" side="top">
          <button
            className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg"
            onClick={async () => {
              try {
                const blob = await fetchGenerationImageBlob(job.id);
                await navigator.clipboard.write([new ClipboardItem({ [blob.type]: blob })]);
              } catch {
                /* clipboard unsupported */
              }
            }}
          >
            <Copy size={15} />
          </button>
        </Tip>
        <Tip label="Открыть крупно" side="top">
          <button className="p-2 rounded-lg text-secondary hover:text-primary hover:bg-hoverbg" onClick={() => setFullscreen(true)}>
            <Maximize2 size={15} />
          </button>
        </Tip>
      </div>

      {/* Fullscreen preview — allowed secondary modal use (UX_SPEC §1) */}
      {fullscreen && url && (
        <div
          className="fixed inset-0 z-[110] flex items-center justify-center bg-black/80"
          onClick={() => setFullscreen(false)}
        >
          <img src={url} alt="" className="max-w-[92vw] max-h-[92vh] object-contain" />
          <button
            className="absolute top-4 right-4 p-2 rounded-lg bg-white/10 text-white"
            onClick={() => setFullscreen(false)}
            aria-label="Закрыть"
          >
            <X size={18} />
          </button>
        </div>
      )}
    </div>
  );
}
